import {generateTokensFromObject} from '../__utils';

export const coreFontFamilies = {
  mono: `ui-monospace,SFMono-Regular,SF Mono,Menlo,Consolas,Liberation Mono,monospace`,
  sansSerif: `Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif`,
  display: `Graphik, Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif`,
  branded: `WF Visual Sans, Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue', Helvetica, Arial, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif`,
};

/** D2 */
export const coreFontWeights = {
  semibold: 600,
  medium: 500,
  normal: 400,
};

/* TBD in D2 */
const coreLetterSpacing = {
  cramped: '-0.006em', // for legacy D1 support
  narrow: '-0.4px',
  normal: '0px',
  wide: '0.5px',
};

export const baseTypography = {
  fontFamily: coreFontFamilies,
  fontWeight: coreFontWeights,
  letterSpacing: coreLetterSpacing,
};

/**
 * We are generating our core tokens here so we can use them to set size context values via CSS Var.
 *
 * For example, `medium` can have `typography.fontFamily.title = var(--wf---system-typography-sansSerif)`
 * To do this, we need to generate the token for use in the `medium.ts` sizing theme.
 *
 * However, we do not set these tokens globally, since we will need to override certain values in specific sizes.
 * For example, `xLargeBranded` needs to set `sansSerif: var(--wf---system-typography-branded)`.
 *
 * This means that the individual size context needs to both get & set its own vars.
 * This is why `prefix: 'typography'` is set here as well, in order to make sure that the vars assigned to these tokens are formed the same way as our size context theme tokens.
 */
export const {
  tokens: {
    fontFamily: fontFamilyTokens,
    fontWeight: fontWeightTokens,
    letterSpacing,
  },
} = generateTokensFromObject(baseTypography, {
  applyNamespace: true,
  prefix: 'typography',
});

export const remify = <T extends number>(px: T): `calc(${T}/16 * 1rem)` =>
  `calc(${px}/16 * 1rem)`;

type RemifiedValue = ReturnType<typeof remify>;

type UITokens = {
  accordion: {
    adornment: {
      rightPosition: string;
    };
    button: {
      padding: {
        comfortable: string;
        compact: string;
      };
    };
  };
  activeCursor: 'pointer' | 'default';
  autocomplete: {
    clearButton: {
      right: string;
    };
    control: {
      padding: string;
    };
    input: {
      height: string;
      padding: string;
    };
    label: {
      virtualized: {
        padding: string;
      };
    };
    listBox: {
      padding: string;
    };
    option: {
      minHeight: string;
      padding: string;
    };
  };
  avatar: {
    xSmall: string;
    small: string;
    medium: string;
    large: string;
    xLarge: string;
  };
  avatarGroup: {
    spacing: {
      xSmall: string;
      small: string;
      medium: string;
      large: string;
      xLarge: string;
    };
    rowItem: {
      margin: string;
      padding: string;
    };
  };
  button: {
    padding: {
      comfortable: string;
      compact: string;
    };
  };
  checkbox: {
    boxSize: string;
    checkSize: string;
  };
  dataTable: {
    filterPopover: {
      filterPopoverSearchPadding: string;
      filterPopoverContentPadding: string;
      filterPopoverSearchEmptyStatePadding: string;
    };
    bulkActionsBar: {
      containerPadding: string;
      labelPadding: string;
      actionsPadding: string;
      dismissSectionPadding: string;
      dividerHeight: string;
    };
  };
  dialog: {
    header: {
      padding: string;
    };
  };
  emptyState: {
    padding: {
      top: string;
      right: string;
      bottom: string;
      left: string;
    };
    margin: {
      button: {
        top: string;
        bottom: string;
      };
      link: {
        top: string;
        bottom: string;
      };
    };
  };
  iconButton: {
    padding: {
      comfortable: string;
      compact: string;
    };
  };
  input: {
    padding: string;
  };
  menu: {
    label: {
      padding: string;
    };
    item: {
      nestedPadding: string;
      padding: string;
      prefix: {height: string};
    };
    content: {
      padding: string;
    };
    divider: {
      margin: string;
    };
  };
  modal: {
    header: {
      padding: string;
    };
  };
  navbar: {
    height: {
      dashboard: string;
      marketplace: string;
    };
  };
  numberInput: {
    input: {
      padding: string;
      height: string;
      withPrefixOrSuffix: {
        paddingX: string;
        minWidth: string;
      };
    };
    controls: {
      width: string;
      height: string;
    };
    stepper: {
      input: {
        width: string;
      };
      button: {
        width: string;
        height: string;
      };
    };
  };
  popover: {
    footer: {
      padding: string;
      spacing: string;
    };
    header: {
      iconButtonHeight: string;
      padding: string;
      spacing: string;
    };
    headerContent: {
      padding: string;
    };
  };
  rowItem: {
    checkbox: {
      marginTop: string;
    };
    dragHandle: {
      checkboxHandleWidth: string;
      paddingLeft: string;
    };
    prefix: {
      marginTop: string;
    };
    padding: {
      comfortable: {
        vertical: string;
        right: string;
        left: string;
      };
      compact: {
        vertical: string;
        right: string;
        left: string;
      };
    };
    spacing: string;
    content: {
      spacing: string;
    };
  };
  segmentedControl: {
    height: string;
  };
  select: {
    control: {
      buttonPadding: string;
      chevronRightPadding: string;
      padding: string;
    };
    content: {
      padding: string;
    };
    option: {
      padding: string;
      spacing: string;
    };
    groupHeader: {
      padding: string;
    };
  };
  switch: {
    track: {
      comfortable: {
        height: string;
        width: string;
      };
      compact: {
        height: string;
        width: string;
      };
    };
    knob: {
      comfortable: {
        height: string;
        width: string;
      };
      compact: {
        height: string;
        width: string;
      };
    };
  };
  table: {
    emptyState: {
      paddingTop: string;
      paddingRight: string;
      paddingBottom: string;
      paddingLeft: string;
    };
    padding: {
      comfortable: string;
      compact: string;
    };
  };
  tabs: {
    tab: {
      height: string;
      margin: string;
      button: {
        comfortable: {
          padding: {
            horizontal: string;
            vertical: string;
            column: string;
          };
        };
        compact: {
          padding: {
            horizontal: string;
            vertical: string;
            column: string;
          };
        };
      };
      underline: {
        comfortable: {
          padding: {
            horizontal: string;
            vertical: string;
            column: string;
          };
        };
        compact: {
          padding: {
            horizontal: string;
            vertical: string;
            column: string;
          };
        };
      };
    };
    tabList: {
      spacing: {
        button: {
          column: string;
          row: string;
        };
        underline: {
          column: string;
          row: string;
        };
      };
    };
    tabPanel: {
      padding: string;
    };
  };
  timeline: {
    header: {
      padding: string;
    };
    body: {
      padding: string;
    };
  };
  toast: {
    body: {
      gap: string;
    };
  };
  tooltip: {
    padding: {
      comfortable: string;
      compact: string;
      commentBubble: string;
    };
  };
};

type FontSizes =
  // System typography styles
  | 'title'
  | 'heading'
  | 'body'
  | 'helpText'
  | 'code'
  // Branded typography styles
  | 'brandedDisplay1'
  | 'brandedDisplay2'
  | 'brandedDisplay3'
  | 'brandedDisplay4'
  | 'brandedDisplay5'
  | 'brandedDisplay6'
  | 'brandedDisplaySubtitle'
  | 'brandedHeading'
  | 'brandedBody'
  | 'brandedHelpText';

export type TypographyTokens = {
  fontSize: {
    [key in FontSizes]:
      | RemifiedValue
      | `clamp(${RemifiedValue}, ${number}vw, ${RemifiedValue})`;
  };
  letterSpacing: typeof letterSpacing;
  lineHeight: {
    [key in FontSizes]: string;
  };
  fontFamily: typeof coreFontFamilies & {
    [key in FontSizes]: string;
  };
  fontWeight: typeof coreFontWeights & {
    [key in FontSizes]: string;
  };
};

export type Sizing = {
  ui: UITokens;
  typography: TypographyTokens;
};
